import { isBefore, isFuture, startOfMonth } from "date-fns";
import { MouseEvent, useEffect, useState } from "react";

import { calendarMonthsShort, disabledMonthMessage } from "./types";
import { Button } from "../button";
import { Tooltip } from "../tooltip";

type MonthPickerProps = {
  year: number;
  month: number;
  setYear: (y: number) => void;
  setMonth: (m: number) => void;
  earliestAllowed?: Date;
};
export const MonthPicker = ({
  year,
  month,
  setYear,
  setMonth,
  earliestAllowed,
}: MonthPickerProps) => {
  const getPrev = () => startOfMonth(new Date(year, month - 1, 1));
  const getNext = () => startOfMonth(new Date(year, month + 1, 1));

  const [disableArrow, setDisableArrow] = useState({
    backward: earliestAllowed ? isBefore(getPrev(), earliestAllowed) : false,
    forward: isFuture(getNext()),
  });

  useEffect(() => {
    setDisableArrow({
      backward: earliestAllowed ? isBefore(getPrev(), earliestAllowed) : false,
      forward: isFuture(getNext()),
    });
  }, [year, month, earliestAllowed]);

  const changeMonth = (byDistance: number) => {
    const newDate = startOfMonth(new Date(year, month + byDistance, 1));
    if (
      (earliestAllowed && isBefore(newDate, earliestAllowed)) ||
      isFuture(newDate)
    )
      return;
    setYear(newDate.getFullYear());
    setMonth(newDate.getMonth());
  };

  // a disabled button that still has focus drops it to the page
  // which closes the date filter in Smokeball Desktop
  const keepFocus = (e: MouseEvent) => e.preventDefault();

  return (
    <div className="flex justify-between items-center">
      <Tooltip text={disableArrow.backward ? disabledMonthMessage : undefined}>
        <Button
          variant="secondarySubtle"
          leftIcon="regularChevronLeft"
          size="small"
          onMouseDown={keepFocus}
          onClick={() => changeMonth(-1)}
          disabled={disableArrow.backward}
          dataTestId="month-picker-back-button"
        />
      </Tooltip>

      <div className="flex gap-small text-ui font-semibold">
        <div>{calendarMonthsShort[month]}</div>
        <div>{year}</div>
      </div>
      <Tooltip text={disableArrow.forward ? disabledMonthMessage : undefined}>
        <Button
          variant="secondarySubtle"
          leftIcon="regularChevronRight"
          size="small"
          onMouseDown={keepFocus}
          onClick={() => changeMonth(1)}
          disabled={disableArrow.forward}
          dataTestId="month-picker-forward-button"
        />
      </Tooltip>
    </div>
  );
};
